import classNames from 'classnames';
import { FormikErrors, FormikTouched, useFormik } from 'formik';
import Input from '../../components/Input/Input';
import { login } from '../../core/auth';
import { isEmail, setTokensToLocalStorage } from '../../core/helpers';
import './Login.scss';
import { ChangeEventHandler, useState } from 'react';
import { Loader } from '../../components/Loader/Loader';
import { useTranslation } from 'react-i18next';
import {
  getAmazonConnectCredentials,
  getUser,
  getUserCompanies,
} from '../../core/api';
import Header from '../../components/Header/Header';
import RegisterCustomer from '../RegisterCustomer/RegisterCustomer';
import { PublicRoutesEnum } from '../../core/enums';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks';
import Button from '../../components/Button';

interface FormValues {
  email: string;
  password: string;
}
const BLOCK_KEY = 'header';

const Login = () => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const { authUser } = useAuth();
  const [error, setError] = useState('');
  const [isValidateEmailShown, setIsValidateEmailShown] = useState(false);

  const formik = useFormik({
    validateOnBlur: true,
    initialValues: {
      email: '',
      password: '',
    },
    validate: (values) => {
      const errors: FormikErrors<FormValues> = {};
      if (!values.email) {
        errors.email = t('Required');
      }
      if (values.email !== '' && !isEmail(values.email)) {
        errors.email = t(`Email is not valid`);
      }
      if (!values.password) {
        errors.password = t('Required');
      }

      return errors;
    },
    onSubmit: ({ email, password }) => {
      if (error) setError('');
      setIsLoading(true);
      login({ email, password })
        .then((res) => {
          setTokensToLocalStorage(
            res.AuthenticationResult!.AccessToken!,
            res.AuthenticationResult!.RefreshToken!,
            res.AuthenticationResult!.IdToken!
          );

          return Promise.all([
            getUser(),
            getAmazonConnectCredentials(),
            getUserCompanies(),
          ]);
        })
        .then((responses) => {
          authUser(responses);
          setIsLoading(false);
        })
        .catch((err) => {
          setIsLoading(false);
          if (
            err.name === 'NotAuthorizedException' ||
            err.name === 'UserNotFoundException'
          ) {
            setError('Incorrect username or password');
          } else if (err.name === 'UserNotConfirmedException') {
            setIsValidateEmailShown(true);
          } else {
            setError('Something went wrong, please try again');
          }
        });
    },
  });

  const onChangeField: ChangeEventHandler<HTMLInputElement> = (e) => {
    if (error) setError('');
    formik.setFieldValue(
      e.target.name,
      e.target.value,
      formik.touched[e.target.name as keyof FormikTouched<FormValues>]
    );
  };

  if (isLoading) return <Loader />;

  if (isValidateEmailShown) {
    return (
      <RegisterCustomer
        initialVerification={true}
        initialEmail={formik.values.email}
        initialPassword={formik.values.password}
      />
    );
  }

  return (
    <>
      <Header blockKey={BLOCK_KEY} />
      <div
        className='Page Page--Login d-flex align-items-center justify-content-center'
        data-testid='login'
      >
        <div className='Login__container mb-lg-5 pb-lg-5'>
          <div className='Title--lg text-lg-center mb-4 fw-900'>
            {t('Welcome')}
          </div>
          <form onSubmit={formik.handleSubmit} noValidate className='w-100'>
            <div className={classNames('Login__content align-items-start')}>
              <Input
                label='Email'
                placeholder='Enter your email'
                name='email'
                id='email'
                onChange={onChangeField}
                onBlur={formik.handleBlur}
                value={formik.values.email}
                error={formik.errors.email}
                touched={formik.touched.email}
              />
              {((!formik.errors.email && formik.touched.email) ||
                !formik.touched.email) && (
                <div className='mt-n3 Text--sm pt-2'>
                  {t('To which you receive emails from vat@countx.com')}
                </div>
              )}
              <Input
                className='mt-3'
                type='password'
                label='Password'
                placeholder='Enter your password'
                name='password'
                id='password'
                onChange={onChangeField}
                onBlur={formik.handleBlur}
                value={formik.values.password}
                touched={formik.touched.password}
                error={formik.errors.password}
              />
              {((!formik.errors.password && formik.touched.password) ||
                !formik.touched.password) && (
                <div
                  className='mt-n3 Text--sm pt-2'
                  style={{
                    maxWidth: 430,
                  }}
                >
                  {t(
                    'Your password contains: min 8 characters, number, special character, uppercase, and lowercase letter'
                  )}
                </div>
              )}
            </div>
            <div>
              <Button
                className='Login__btn mt-4 w-100'
                type='submit'
                disabled={isLoading}
              >
                {t('Sign In')}
              </Button>
              {error && (
                <div className='Login__error mt-2 text-center'>{t(error)}</div>
              )}
            </div>
          </form>
          <div className='text-center mt-4 pt-2 Text'>
            <Link to={PublicRoutesEnum.ForgotPassword} className='Link--blue'>
              {t('I forgot my password')}
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;
